import {
  getPasswordStrength,
  type PasswordStrengthLevel,
} from 'api/utils/passwordStrength'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

type PasswordStrengthHintProps = {
  password: string
}

const strengthBarClass: Record<PasswordStrengthLevel, string> = {
  empty: 'bg-gray-200',
  weak: 'bg-red-500',
  medium: 'bg-amber-500',
  strong: 'bg-green-500',
}

const PasswordStrengthHint = ({ password }: PasswordStrengthHintProps) => {
  const { t } = useTranslation()
  const strength = getPasswordStrength(password)
  const filled =
    strength === 'strong' ? 3 : strength === 'medium' ? 2 : strength === 'weak' ? 1 : 0

  const requirements = [
    { key: 'length', met: password.length >= 8 },
    { key: 'letter', met: /[a-zA-Z]/.test(password) },
    { key: 'digit', met: /\d/.test(password) },
    { key: 'special', met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) },
  ] as const

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-gray-600">
          {t('auth.passwordRequirements.title')}
        </span>
        {password ? (
          <span
            className={classNames(
              'font-bold uppercase tracking-wide',
              strength === 'weak' && 'text-red-600',
              strength === 'medium' && 'text-amber-600',
              strength === 'strong' && 'text-green-700'
            )}
          >
            {t(`auth.passwordStrength.${strength}`)}
          </span>
        ) : null}
      </div>
      <div className="flex gap-1">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            className={classNames(
              'h-1 flex-1 rounded-full transition-colors',
              i < filled ? strengthBarClass[strength] : 'bg-gray-200'
            )}
          />
        ))}
      </div>
      <ul className="space-y-0.5 text-gray-600">
        {requirements.map(req => (
          <li
            key={req.key}
            className={classNames(
              'flex items-center gap-1.5',
              req.met ? 'text-green-700' : undefined
            )}
          >
            <span aria-hidden>{req.met ? '✓' : '○'}</span>
            {t(`auth.passwordRequirements.${req.key}`)}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PasswordStrengthHint
